import type { BaseEvent, EventType } from '../../events/base';
import type { RuntypeBase } from 'runtypes/lib/runtype';
import { ClickstreamEventEnum } from 'bc-model';
import type { PageViewedBrowserEvent } from '../../events/page-viewed';

import { PageViewedEventBrowserRuntype } from '../../events/page-viewed';
import {
  ExceptionUnhandledBrowserRuntype,
  type ExceptionUnhandledBrowserEvent,
} from '../../events/exception-unhandled';
import {
  AiShorthandRequestedEventBrowserRuntype,
  type AiShorthandRequestedBrowserEvent,
} from '../../events/ai-shorthand-requested';
import {
  AiShorthandAcceptedEventBrowserRuntype,
  type AiShorthandAcceptedBrowserEvent,
} from '../../events/ai-shorthand-accepted';
import {
  AiShorthandRejectedEventBrowserRuntype,
  type AiShorthandRejectedBrowserEvent,
} from '../../events/ai-shorthand-rejected';
import {
  AiShorthandAbandonedEventBrowserRuntype,
  type AiShorthandAbandonedBrowserEvent,
} from '../../events/ai-shorthand-abandoned';
import {
  type AiShorthandReceivedBrowserEvent,
  AiShorthandReceivedEventBrowserRuntype,
} from '../../events/ai-shorthand-received';
import {
  type AiShorthandFailedBrowserEvent,
  AiShorthandFailedEventBrowserRuntype,
} from '../../events/ai-shorthand-failed';
import {
  AiChatRequestedEventBrowserRuntype,
  type AiChatRequestedBrowserEvent,
} from '../../events/ai-chat-requested';
import {
  AiChatReceivedEventBrowserRuntype,
  type AiChatReceivedBrowserEvent,
} from '../../events/ai-chat-received';
import {
  AiChatAbandonedEventBrowserRuntype,
  type AiChatAbandonedBrowserEvent,
} from '../../events/ai-chat-abandoned';
import {
  AiChatFailedEventBrowserRuntype,
  type AiChatFailedBrowserEvent,
} from '../../events/ai-chat-failed';
import {
  AiChatThumbsupEventBrowserRuntype,
  type AiChatThumbsupBrowserEvent,
} from '../../events/ai-chat-thumbsup';
import {
  AiChatThumbsdownEventBrowserRuntype,
  type AiChatThumbsdownBrowserEvent,
} from '../../events/ai-chat-thumbsdown';
import {
  AiChatRetryEventBrowserRuntype,
  type AiChatRetryBrowserEvent,
} from '../../events/ai-chat-retry';
import {
  type MenuItemClickedBrowserEvent,
  MenuItemClickedEventBrowserRuntype,
} from '../../events/menu-item-clicked';
import {
  type NoticeDismissedBrowserEvent,
  NoticeDismissedBrowserRunType,
} from '../../events/notice-dismissed';
import { type NoticeMutedBrowserEvent, NoticeMutedBrowserRunType } from '../../events/notice-muted';
import {
  type NoticeSnoozedBrowserEvent,
  NoticeSnoozedBrowserRunType,
} from '../../events/notice-snoozed';

type BrowserEventType = Extract<
  EventType,
  | ClickstreamEventEnum.EXCEPTION_UNHANDLED
  | ClickstreamEventEnum.PAGE_VIEWED
  | ClickstreamEventEnum.AI_SHORTHAND_REQUESTED
  | ClickstreamEventEnum.AI_SHORTHAND_RECEIVED
  | ClickstreamEventEnum.AI_SHORTHAND_ACCEPTED
  | ClickstreamEventEnum.AI_SHORTHAND_REJECTED
  | ClickstreamEventEnum.AI_SHORTHAND_ABANDONED
  | ClickstreamEventEnum.AI_SHORTHAND_FAILED
  | ClickstreamEventEnum.AI_ASSIST_REQUESTED
  | ClickstreamEventEnum.AI_ASSIST_RECEIVED
  | ClickstreamEventEnum.AI_ASSIST_ABANDONED
  | ClickstreamEventEnum.AI_ASSIST_FAILED
  | ClickstreamEventEnum.AI_ASSIST_THUMBS_UP
  | ClickstreamEventEnum.AI_ASSIST_THUMBS_DOWN
  | ClickstreamEventEnum.AI_ASSIST_RETRY
  | ClickstreamEventEnum.MENU_ITEM_CLICKED
  | ClickstreamEventEnum.NOTICE_DISMISSED
  | ClickstreamEventEnum.NOTICE_MUTED
  | ClickstreamEventEnum.NOTICE_SNOOZED
>;

type CallbackArgs = {
  [ClickstreamEventEnum.PAGE_VIEWED]: PageViewedBrowserEvent;
  [ClickstreamEventEnum.MENU_ITEM_CLICKED]: MenuItemClickedBrowserEvent;
  [ClickstreamEventEnum.EXCEPTION_UNHANDLED]: ExceptionUnhandledBrowserEvent;
  [ClickstreamEventEnum.AI_SHORTHAND_REQUESTED]: AiShorthandRequestedBrowserEvent;
  [ClickstreamEventEnum.AI_SHORTHAND_RECEIVED]: AiShorthandReceivedBrowserEvent;
  [ClickstreamEventEnum.AI_SHORTHAND_ACCEPTED]: AiShorthandAcceptedBrowserEvent;
  [ClickstreamEventEnum.AI_SHORTHAND_REJECTED]: AiShorthandRejectedBrowserEvent;
  [ClickstreamEventEnum.AI_SHORTHAND_ABANDONED]: AiShorthandAbandonedBrowserEvent;
  [ClickstreamEventEnum.AI_SHORTHAND_FAILED]: AiShorthandFailedBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_REQUESTED]: AiChatRequestedBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_RECEIVED]: AiChatReceivedBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_ABANDONED]: AiChatAbandonedBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_FAILED]: AiChatFailedBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_THUMBS_UP]: AiChatThumbsupBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_THUMBS_DOWN]: AiChatThumbsdownBrowserEvent;
  [ClickstreamEventEnum.AI_ASSIST_RETRY]: AiChatRetryBrowserEvent;
  [ClickstreamEventEnum.NOTICE_DISMISSED]: NoticeDismissedBrowserEvent;
  [ClickstreamEventEnum.NOTICE_MUTED]: NoticeMutedBrowserEvent;
  [ClickstreamEventEnum.NOTICE_SNOOZED]: NoticeSnoozedBrowserEvent;
};

type TrackerArgs = {
  [ClickstreamEventEnum.PAGE_VIEWED]: never;
  [ClickstreamEventEnum.EXCEPTION_UNHANDLED]: Pick<
    ExceptionUnhandledBrowserEvent,
    'message' | 'name' | 'stack'
  >;
  [ClickstreamEventEnum.AI_SHORTHAND_REQUESTED]: Pick<
    AiShorthandRequestedBrowserEvent,
    'nodeId' | 'originalText'
  >;
  [ClickstreamEventEnum.AI_SHORTHAND_ACCEPTED]: Pick<
    AiShorthandAcceptedBrowserEvent,
    'nodeId' | 'originalText' | 'summaryText'
  >;
  [ClickstreamEventEnum.AI_SHORTHAND_RECEIVED]: Pick<
    AiShorthandRejectedBrowserEvent,
    'nodeId' | 'originalText' | 'summaryText'
  >;
  [ClickstreamEventEnum.AI_SHORTHAND_REJECTED]: Pick<
    AiShorthandRejectedBrowserEvent,
    'nodeId' | 'originalText' | 'summaryText'
  >;
  [ClickstreamEventEnum.AI_SHORTHAND_ABANDONED]: Pick<
    AiShorthandAbandonedBrowserEvent,
    'nodeId' | 'originalText'
  >;
  [ClickstreamEventEnum.AI_SHORTHAND_FAILED]: Pick<
    AiShorthandFailedBrowserEvent,
    'nodeId' | 'originalText'
  >;
  [ClickstreamEventEnum.AI_ASSIST_REQUESTED]: Pick<
    AiChatRequestedBrowserEvent,
    'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.AI_ASSIST_RECEIVED]: Pick<
    AiChatReceivedBrowserEvent,
    'response' | 'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.AI_ASSIST_ABANDONED]: Pick<
    AiChatAbandonedBrowserEvent,
    'response' | 'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.AI_ASSIST_FAILED]: Pick<
    AiChatFailedBrowserEvent,
    'response' | 'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.AI_ASSIST_THUMBS_UP]: Pick<
    AiChatThumbsupBrowserEvent,
    'response' | 'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.AI_ASSIST_THUMBS_DOWN]: Pick<
    AiChatThumbsdownBrowserEvent,
    'response' | 'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.AI_ASSIST_RETRY]: Pick<
    AiChatRetryBrowserEvent,
    'response' | 'contextMetadata' | 'message'
  >;
  [ClickstreamEventEnum.MENU_ITEM_CLICKED]: Pick<MenuItemClickedBrowserEvent, 'path'>;
  [ClickstreamEventEnum.NOTICE_DISMISSED]: Pick<
    NoticeDismissedBrowserEvent,
    'noticeKey' | 'noticeType'
  >;
  [ClickstreamEventEnum.NOTICE_MUTED]: Pick<NoticeMutedBrowserEvent, 'noticeKey' | 'noticeType'>;
  [ClickstreamEventEnum.NOTICE_SNOOZED]: Pick<
    NoticeSnoozedBrowserEvent,
    'noticeKey' | 'noticeType'
  >;
};

type BrowserTrackerCallbackEvent = CallbackArgs[BrowserEventType] & BaseEvent;

type BrowserTrackerCallback = (
  err: Nullable<Error>,
  event: Nullable<BrowserTrackerCallbackEvent>
) => void;

type BrowserEventTrackers = {
  [K in BrowserEventType]: ((data: TrackerArgs[K]) => void) | (() => void);
};

const createAndValidateEvent = <T extends BrowserEventType>(
  data: CallbackArgs[T],
  runtype: RuntypeBase<CallbackArgs[T] & BaseEvent>
): [Nullable<Error>, Nullable<BrowserTrackerCallbackEvent>] => {
  let err: Nullable<Error> = null;
  let event: Nullable<BrowserTrackerCallbackEvent> = null;
  try {
    event = runtype.check({
      ...data,
    }) as unknown as Nullable<BrowserTrackerCallbackEvent>;
  } catch (e) {
    err = new Error('Invalid event data');
    if (e instanceof Error) {
      err = e;
    } else if (typeof e === 'string') {
      err = new Error(e);
    }
  }
  return [err, event];
};

const trackers = (callback: BrowserTrackerCallback): BrowserEventTrackers => {
  return {
    [ClickstreamEventEnum.EXCEPTION_UNHANDLED]: (data) => {
      const augmentedData: ExceptionUnhandledBrowserEvent = {
        sentAt: new Date().toISOString(),
        event: ClickstreamEventEnum.EXCEPTION_UNHANDLED,
        message: data.message,
        name: data.name,
        source: 'browser',
        stack: data.stack,
        version: 2,
        url: window.location.href,
      };
      const [err, event] = createAndValidateEvent(augmentedData, ExceptionUnhandledBrowserRuntype);
      callback(err, event);
    },
    [ClickstreamEventEnum.PAGE_VIEWED]: () => {
      const augmentedData: PageViewedBrowserEvent = {
        event: ClickstreamEventEnum.PAGE_VIEWED,
        path: window.location.pathname,
        referrer: document.referrer,
        search: window.location.search,
        sentAt: new Date().toISOString(),
        title: document.title,
        url: window.location.href,
        version: 2,
      };
      const [err, event] = createAndValidateEvent(augmentedData, PageViewedEventBrowserRuntype);
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_SHORTHAND_REQUESTED]: (data) => {
      const augmentedData: AiShorthandRequestedBrowserEvent = {
        event: ClickstreamEventEnum.AI_SHORTHAND_REQUESTED,
        sentAt: new Date().toISOString(),
        nodeId: data.nodeId,
        originalText: data.originalText,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiShorthandRequestedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_SHORTHAND_RECEIVED]: (data) => {
      const augmentedData: AiShorthandReceivedBrowserEvent = {
        event: ClickstreamEventEnum.AI_SHORTHAND_RECEIVED,
        sentAt: new Date().toISOString(),
        nodeId: data.nodeId,
        originalText: data.originalText,
        summaryText: data.summaryText,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiShorthandReceivedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_SHORTHAND_ACCEPTED]: (data) => {
      const augmentedData: AiShorthandAcceptedBrowserEvent = {
        event: ClickstreamEventEnum.AI_SHORTHAND_ACCEPTED,
        sentAt: new Date().toISOString(),
        nodeId: data.nodeId,
        originalText: data.originalText,
        summaryText: data.summaryText,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiShorthandAcceptedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_SHORTHAND_REJECTED]: (data) => {
      const augmentedData: AiShorthandRejectedBrowserEvent = {
        event: ClickstreamEventEnum.AI_SHORTHAND_REJECTED,
        sentAt: new Date().toISOString(),
        nodeId: data.nodeId,
        originalText: data.originalText,
        summaryText: data.summaryText,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiShorthandRejectedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_SHORTHAND_ABANDONED]: (data) => {
      const augmentedData: AiShorthandAbandonedBrowserEvent = {
        event: ClickstreamEventEnum.AI_SHORTHAND_ABANDONED,
        sentAt: new Date().toISOString(),
        nodeId: data.nodeId,
        originalText: data.originalText,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiShorthandAbandonedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_SHORTHAND_FAILED]: (data) => {
      const augmentedData: AiShorthandFailedBrowserEvent = {
        event: ClickstreamEventEnum.AI_SHORTHAND_FAILED,
        sentAt: new Date().toISOString(),
        nodeId: data.nodeId,
        originalText: data.originalText,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiShorthandFailedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_FAILED]: (data) => {
      const augmentedData: AiChatFailedBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_FAILED,
        sentAt: new Date().toISOString(),
        response: data.response,
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, AiChatFailedEventBrowserRuntype);
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_ABANDONED]: (data) => {
      const augmentedData: AiChatAbandonedBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_ABANDONED,
        sentAt: new Date().toISOString(),
        response: data.response,
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiChatAbandonedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_RECEIVED]: (data) => {
      const augmentedData: AiChatReceivedBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_RECEIVED,
        sentAt: new Date().toISOString(),
        response: data.response,
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, AiChatReceivedEventBrowserRuntype);
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_REQUESTED]: (data) => {
      const augmentedData: AiChatRequestedBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_REQUESTED,
        sentAt: new Date().toISOString(),
        response: '',
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiChatRequestedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_THUMBS_UP]: (data) => {
      const augmentedData: AiChatThumbsupBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_THUMBS_UP,
        sentAt: new Date().toISOString(),
        response: data.response,
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, AiChatThumbsupEventBrowserRuntype);
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_THUMBS_DOWN]: (data) => {
      const augmentedData: AiChatThumbsdownBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_THUMBS_DOWN,
        sentAt: new Date().toISOString(),
        response: data.response,
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        AiChatThumbsdownEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.AI_ASSIST_RETRY]: (data) => {
      const augmentedData: AiChatRetryBrowserEvent = {
        event: ClickstreamEventEnum.AI_ASSIST_RETRY,
        sentAt: new Date().toISOString(),
        response: data.response,
        contextMetadata: data.contextMetadata,
        message: data.message,
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, AiChatRetryEventBrowserRuntype);
      callback(err, event);
    },
    [ClickstreamEventEnum.MENU_ITEM_CLICKED]: (data) => {
      const augmentedData: MenuItemClickedBrowserEvent = {
        event: ClickstreamEventEnum.MENU_ITEM_CLICKED,
        path: data.path,
        url: window.location.href,
        sentAt: new Date().toISOString(),
        version: 1,
      };
      const [err, event] = createAndValidateEvent(
        augmentedData,
        MenuItemClickedEventBrowserRuntype
      );
      callback(err, event);
    },
    [ClickstreamEventEnum.NOTICE_DISMISSED]: (data) => {
      const augmentedData: NoticeDismissedBrowserEvent = {
        event: ClickstreamEventEnum.NOTICE_DISMISSED,
        noticeKey: data.noticeKey,
        noticeType: data.noticeType,
        sentAt: new Date().toISOString(),
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, NoticeDismissedBrowserRunType);
      callback(err, event);
    },
    [ClickstreamEventEnum.NOTICE_MUTED]: (data) => {
      const augmentedData: NoticeMutedBrowserEvent = {
        event: ClickstreamEventEnum.NOTICE_MUTED,
        noticeKey: data.noticeKey,
        noticeType: data.noticeType,
        sentAt: new Date().toISOString(),
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, NoticeMutedBrowserRunType);
      callback(err, event);
    },
    [ClickstreamEventEnum.NOTICE_SNOOZED]: (data) => {
      const augmentedData: NoticeSnoozedBrowserEvent = {
        event: ClickstreamEventEnum.NOTICE_SNOOZED,
        noticeKey: data.noticeKey,
        noticeType: data.noticeType,
        sentAt: new Date().toISOString(),
        version: 1,
      };
      const [err, event] = createAndValidateEvent(augmentedData, NoticeSnoozedBrowserRunType);
      callback(err, event);
    },
  };
};

type BrowserTracker = {
  track: {
    <K extends BrowserEventType>(type: K, data?: TrackerArgs[K]): void;
  };
};

const browserTracker = (callback: BrowserTrackerCallback): BrowserTracker => {
  return {
    track: <K extends BrowserEventType>(type: K, data?: TrackerArgs[K]) => {
      trackers(callback)[type](data as TrackerArgs[K]);
    },
  };
};

export { browserTracker };
export type { BrowserTrackerCallback, BrowserTrackerCallbackEvent, BrowserEventType, TrackerArgs };
